import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Typography,
    CircularProgress,
} from '@mui/material';
import { post, put, get, del } from 'src/utils/networkUtils';
import { uploadImageAndGetUrl } from 'src/utils/uploadImage';
import ClearIcon from '@mui/icons-material/Clear';
import { IconButton } from '@mui/material';

// ----------------------------------------------------------------------

TenantDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    tenant: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
};

export default function TenantDialog({ open, tenant, onClose, onSuccess }) {
    const [formData, setFormData] = useState({
        name: '',
        shortCode: '',
        sportType: 0, // Default sportType
        logoUrl: null,
        bannerImageUrl: null,
        playgroundImageUrl: null,
        isDefaultTenant: false, // Default to false for new tenants
    });
    const [competitions, setCompetitions] = useState([]);
    const [selectedCompetitions, setSelectedCompetitions] = useState([]);
    const [tenantCompetitions, setTenantCompetitions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingCompetitions, setLoadingCompetitions] = useState(false);
    const [errors, setErrors] = useState({});

    const isEditMode = Boolean(tenant);

    const [images, setImages] = useState({
        logo: { file: null, preview: '', modified: false },
        banner: { file: null, preview: '', modified: false },
        playground: { file: null, preview: '', modified: false },
    });

    const logoInputRef = useRef(null);
    const bannerInputRef = useRef(null);
    const playgroundInputRef = useRef(null);
    const loadCompetitions = useCallback(async () => {
        setLoadingCompetitions(true);
        try {
            const response = await get('/admin/competitions');
            setCompetitions(response.competitions || []);
        } catch (error) {
            console.error('Error loading competitions:', error);
            setErrors({ general: 'Failed to load competitions' });
        } finally {
            setLoadingCompetitions(false);
        }
    }, []);

    const loadTenantCompetitions = useCallback(async () => {
        if (!tenant?.tenantId) return;

        try {
            const response = await get(`/admin/tenants/${tenant.tenantId}/tenant-competitions`);
            const tenantComps = response.tenantCompetitions || [];
            setTenantCompetitions(tenantComps);

            // Set selected competitions based on existing associations
            const selectedIds = tenantComps.map(tc => tc.competitionId);
            setSelectedCompetitions(selectedIds);
        } catch (error) {
            console.error('Error loading tenant competitions:', error);
        }
    }, [tenant?.tenantId]);

    // Load competitions and tenant competitions when dialog opens
    useEffect(() => {
        if (open) {
            loadCompetitions();
            if (tenant) {
                loadTenantCompetitions();
            }
        }
    }, [open, tenant, loadCompetitions, loadTenantCompetitions]);

    useEffect(() => {
        if (tenant) {
            setFormData({
                name: tenant.name || '',
                shortCode: tenant.shortCode || '',
                sportType: (typeof tenant.sportType === 'number') ? tenant.sportType : (tenant.sportType === 'Soccer' ? 1 : 0),
                logoUrl: tenant.logoUrl ?? null,
                bannerImageUrl: tenant.bannerImageUrl ?? null,
                playgroundImageUrl: tenant.playgroundImageUrl ?? null,
                isDefaultTenant: tenant.isDefaultTenant === true || tenant.isDefaultTenant === 1,
            });
            setImages({
                logo: { file: null, preview: tenant.logoUrl || '', modified: false },
                banner: { file: null, preview: tenant.bannerImageUrl || '', modified: false },
                playground: { file: null, preview: tenant.playgroundImageUrl || '', modified: false },
            });
        } else {
            setFormData({
                name: '',
                shortCode: '',
                sportType: 0,
                logoUrl: null,
                bannerImageUrl: null,
                playgroundImageUrl: null,
                isDefaultTenant: false, // New tenants default to false
            });
            setSelectedCompetitions([]);
            setTenantCompetitions([]);
            setImages({
                logo: { file: null, preview: '', modified: false },
                banner: { file: null, preview: '', modified: false },
                playground: { file: null, preview: '', modified: false },
            });
        }
        setErrors({});
    }, [tenant]);

    const handleCompetitionChange = (event) => {
        const value = event.target.value;
        const valuesArray = typeof value === 'string' ? value.split(',') : value;
        setSelectedCompetitions(valuesArray.map(v => Number(v)));
    };

    const manageTenantCompetitions = async (tenantId, newCompetitionIds) => {
        const currentCompetitionIds = tenantCompetitions.map(tc => tc.competitionId);

        // Find competitions to add
        const toAdd = newCompetitionIds.filter(id => !currentCompetitionIds.includes(id));

        // Find competitions to remove  
        const toRemove = currentCompetitionIds.filter(id => !newCompetitionIds.includes(id));

        // Add new associations
        for (const competitionId of toAdd) {
            await post('/admin/tenant-competitions', {
                tenantId,
                competitionId: parseInt(competitionId),
            });
        }

        // Remove old associations
        for (const competitionId of toRemove) {
            const tenantCompetition = tenantCompetitions.find(tc => +tc.competitionId === +competitionId);
            if (tenantCompetition) {
                await del(`/admin/tenant-competitions/${tenantCompetition.tenantCompetitionId}`);
            }
        }
    };
    const handleChange = (field) => (event) => {
        let value = event.target.value;

        // Format short code to uppercase and remove spaces (but keep underscores)
        if (field === 'shortCode') {
            value = value.toUpperCase().replace(/\s/g, '');
        }

        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const validateAndStageImage = (imageType) => (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        const maxSize = 2 * 1024 * 1024; // 2MB
        if (!validTypes.includes(file.type)) {
            alert('Only JPG, JPEG, PNG, and WEBP formats are allowed.');
            return;
        }
        if (file.size > maxSize) {
            alert('File size should not exceed 2MB.');
            return;
        }
        setImages(prev => ({
            ...prev,
            [imageType]: {
                file,
                preview: URL.createObjectURL(file),
                modified: true,
            },
        }));
        // Allow selecting the same file again by clearing the input value
        event.target.value = null;
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Tenant name is required';
        }

        if (formData.shortCode && !/^[A-Z0-9_]*$/.test(formData.shortCode)) {
            newErrors.shortCode = 'Short code must contain only uppercase letters, numbers, and underscores';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            // Prepare submit data
            const submitData = {
                ...formData,
                shortCode: formData.shortCode.trim() || null,
                sportType: Number(formData.sportType), // Coerce sportType to number
                isDefaultTenant: Boolean(formData.isDefaultTenant),
            };

            const imageMapping = {
                logo: 'logoUrl',
                banner: 'bannerImageUrl',
                playground: 'playgroundImageUrl',
            };

            for (const [imageType, fieldName] of Object.entries(imageMapping)) {
                const image = images[imageType];
                if (image.modified) {
                    submitData[fieldName] = image.file ? await uploadImageAndGetUrl(image.file) : null;
                }
            }

            // Normalize any accidental empty strings to null
            submitData.logoUrl = submitData.logoUrl || null;
            submitData.bannerImageUrl = submitData.bannerImageUrl || null;
            submitData.playgroundImageUrl = submitData.playgroundImageUrl || null;

            let savedTenant;
            if (isEditMode) {
                // Edit existing tenant
                const response = await put(`/admin/tenants/${tenant.tenantId}`, submitData);
                savedTenant = response.tenant;

                // Manage competition associations for existing tenant
                await manageTenantCompetitions(tenant.tenantId, selectedCompetitions);
            } else {
                // Create new tenant
                const response = await post(`/admin/tenants`, submitData);
                savedTenant = response.tenant;

                // Add competition associations for new tenant
                if (selectedCompetitions.length > 0) {
                    await manageTenantCompetitions(savedTenant.tenantId, selectedCompetitions);
                }
            }
            onSuccess();
        } catch (error) {
            console.error('Error saving tenant:', error);

            // Handle specific error cases
            if (error.response?.status === 400) {
                const errorMessage = error.response?.data?.error || 'Validation error';
                if (errorMessage.includes('short code')) {
                    setErrors({ shortCode: 'This short code already exists' });
                } else {
                    setErrors({ general: errorMessage });
                }
            } else {
                setErrors({ general: 'Failed to save tenant. Please try again.' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const isFormValid = formData.name && formData.name.trim();

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {isEditMode ? 'Edit Tenant' : 'Create Tenant'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    {errors.general && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errors.general}
                        </Alert>
                    )}
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="sporttype-label">Sport</InputLabel>
                                <Select
                                    labelId="sporttype-label"
                                    value={formData.sportType}
                                    label="Sport"
                                    onChange={(e) => setFormData(prev => ({ ...prev, sportType: Number(e.target.value) }))}
                                >
                                    <MenuItem value={0}>Hockey</MenuItem>
                                    <MenuItem value={1}>Soccer</MenuItem>
                                    <MenuItem value={2}>Football</MenuItem>
                                    <MenuItem value={3}>Basketball</MenuItem>
                                    <MenuItem value={4}>Cricket</MenuItem>
                                    <MenuItem value={5}>Australian Football</MenuItem>
                                    <MenuItem value={6}>Rugby</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {isEditMode && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="isdefault-label">Set as Default</InputLabel>
                                    <Select
                                        labelId="isdefault-label"
                                        value={formData.isDefaultTenant ? 1 : 0}
                                        label="Set as Default"
                                        onChange={(e) => setFormData(prev => ({ ...prev, isDefaultTenant: Number(e.target.value) === 1 }))}
                                        disabled={loading}
                                    >
                                        <MenuItem value={1}>True</MenuItem>
                                        <MenuItem value={0}>False</MenuItem>
                                    </Select>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Only one tenant can be set as default at a time. If you set this tenant as default, any other default tenant will be unset.
                                    </Typography>
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                label="Tenant Name"
                                value={formData.name}
                                onChange={handleChange('name')}
                                fullWidth
                                required
                                disabled={loading}
                                placeholder="e.g., Premier League"
                                error={Boolean(errors.name)}
                                helperText={errors.name}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Short Code"
                                value={formData.shortCode}
                                onChange={handleChange('shortCode')}
                                fullWidth
                                disabled={loading}
                                placeholder="e.g., PL or FR_L1"
                                error={Boolean(errors.shortCode)}
                                helperText={errors.shortCode || "Optional unique identifier for this tenant (letters, numbers, underscores)"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <label>
                                            <input ref={logoInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" style={{ display: 'none' }} onChange={validateAndStageImage('logo')} disabled={loading} />
                                            <Button component="span" variant="outlined" disabled={loading}>Choose Logo</Button>
                                        </label>
                                        {images.logo.preview && (
                                            <IconButton color="error" disabled={loading} onClick={() => { setImages(prev => ({ ...prev, logo: { file: null, preview: '', modified: true } })); setFormData(prev => ({ ...prev, logoUrl: null })); if (logoInputRef.current) logoInputRef.current.value = null; }}>
                                                <ClearIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Box sx={{ width: 160, height: 80, borderRadius: 1, overflow: 'hidden', bgcolor: 'background.neutral', border: '1px solid', borderColor: 'divider' }}>
                                        {images.logo.preview ? (
                                            <img src={images.logo.preview} alt="logo preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>No image</Box>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <label>
                                            <input ref={bannerInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" style={{ display: 'none' }} onChange={validateAndStageImage('banner')} disabled={loading} />
                                            <Button component="span" variant="outlined" disabled={loading}>Choose Banner</Button>
                                        </label>
                                        {images.banner.preview && (
                                            <IconButton color="error" disabled={loading} onClick={() => { setImages(prev => ({ ...prev, banner: { file: null, preview: '', modified: true } })); setFormData(prev => ({ ...prev, bannerImageUrl: null })); if (bannerInputRef.current) bannerInputRef.current.value = null; }}>
                                                <ClearIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Box sx={{ width: 160, height: 80, borderRadius: 1, overflow: 'hidden', bgcolor: 'background.neutral', border: '1px solid', borderColor: 'divider' }}>
                                        {images.banner.preview ? (
                                            <img src={images.banner.preview} alt="banner preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>No image</Box>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <label>
                                            <input ref={playgroundInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" style={{ display: 'none' }} onChange={validateAndStageImage('playground')} disabled={loading} />
                                            <Button component="span" variant="outlined" disabled={loading}>Choose Playground</Button>
                                        </label>
                                        {images.playground.preview && (
                                            <IconButton color="error" disabled={loading} onClick={() => { setImages(prev => ({ ...prev, playground: { file: null, preview: '', modified: true } })); setFormData(prev => ({ ...prev, playgroundImageUrl: null })); if (playgroundInputRef.current) playgroundInputRef.current.value = null; }}>
                                                <ClearIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Box sx={{ width: 160, height: 80, borderRadius: 1, overflow: 'hidden', bgcolor: 'background.neutral', border: '1px solid', borderColor: 'divider' }}>
                                        {images.playground.preview ? (
                                            <img src={images.playground.preview} alt="playground preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>No image</Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth disabled={loading || loadingCompetitions}>
                                <InputLabel id="competitions-label">Competitions</InputLabel>
                                <Select
                                    labelId="competitions-label"
                                    multiple
                                    value={selectedCompetitions}
                                    onChange={handleCompetitionChange}
                                    label="Competitions"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const competition = competitions.find(c => c.competitionId === value);
                                                return (
                                                    <Chip
                                                        key={value}
                                                        label={competition?.name || `Competition ${value}`}
                                                        size="small"
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                >
                                    {loadingCompetitions ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Loading competitions...
                                        </MenuItem>
                                    ) : competitions.length === 0 ? (
                                        <MenuItem disabled>
                                            No competitions available
                                        </MenuItem>
                                    ) : (
                                        competitions.map((competition) => (
                                            <MenuItem
                                                key={competition.competitionId}
                                                value={competition.competitionId}
                                            >
                                                {competition.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Select competitions to associate with this tenant
                                </Typography>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !isFormValid}
                >
                    {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
